import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  ShieldCheck,
  FileText,
  Calendar,
  Users,
  Zap,
  ChevronRight,
  Activity,
  Rocket,
} from 'lucide-react';
import { motion } from 'framer-motion';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      delay: i * 0.15,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
    },
  }),
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } },
};

const Home: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background overflow-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3">
            <div className="flex items-center gap-2">
              <img
                src="/lovable-uploads/39c9b322-1952-4f60-949b-38a0a810a383.png"
                alt="MediCert Logo"
                className="h-12 w-auto"
                fetchPriority="high"
                decoding="async"
                width={48}
                height={48}
              />
            </div>
            <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-muted-foreground">
              <a href="#features" className="hover:text-foreground transition-colors">
                Funkcje
              </a>
              <a href="#how-it-works" className="hover:text-foreground transition-colors">
                Jak to działa
              </a>
            </nav>
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" size="sm" className="text-muted-foreground">
                  Zaloguj się
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-grow w-full">
        {/* Hero */}
        <section className="relative pt-32 pb-20 md:pt-44 md:pb-32 overflow-hidden">
          <div className="absolute inset-0 -z-10">
            <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full bg-primary/8 blur-[120px]" />
            <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full bg-secondary/10 blur-[100px]" />
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              className="max-w-4xl mx-auto text-center"
              initial="hidden"
              animate="visible"
              variants={stagger}
            >
              <motion.div variants={fadeUp} custom={0}>
                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                  <Rocket className="h-3.5 w-3.5" />
                  MVP — Start Q2 2026
                </span>
              </motion.div>

              <motion.h1
                variants={fadeUp}
                custom={1}
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-foreground leading-[1.1] mb-6"
              >
                Cyfrowy gabinet
                <br />
                <span className="bg-gradient-to-r from-primary via-primary to-secondary bg-clip-text text-transparent">
                  badań kierowców
                </span>
              </motion.h1>

              <motion.p
                variants={fadeUp}
                custom={2}
                className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed"
              >
                Budujemy pierwszy w Polsce kompletny system do zarządzania gabinetem badań kierowców
                — od rejestracji pacjenta, przez badanie, po wydanie orzeczenia. Dołącz do programu
                wczesnego dostępu.
              </motion.p>

              <motion.div
                variants={fadeUp}
                custom={3}
                className="flex flex-col sm:flex-row gap-4 justify-center"
              >
                <Link to="/login">
                  <Button
                    size="lg"
                    className="rounded-full px-8 h-12 text-base shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all"
                  >
                    Zaloguj się
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </motion.div>
            </motion.div>

            {/* MVP Roadmap instead of fake stats */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="mt-20 max-w-3xl mx-auto"
            >
              <div className="grid grid-cols-3 gap-4">
                {[
                  { value: 'Q2 2026', label: 'Start programu beta', icon: Rocket },
                  { value: 'Q3 2026', label: 'Publiczna premiera', icon: Zap },
                  { value: '100%', label: 'Zgodność z przepisami', icon: ShieldCheck },
                ].map((stat, i) => (
                  <div
                    key={i}
                    className="text-center p-4 sm:p-6 rounded-2xl bg-card/50 backdrop-blur-sm border border-border/50"
                  >
                    <stat.icon className="h-5 w-5 text-primary mx-auto mb-2" />
                    <div className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground">
                      {stat.value}
                    </div>
                    <div className="text-xs sm:text-sm text-muted-foreground mt-1">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features */}
        <section id="features" className="py-24 relative">
          <div className="absolute inset-0 -z-10 bg-muted/30" />
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-100px' }}
              variants={stagger}
              className="text-center mb-16"
            >
              <motion.span
                variants={fadeUp}
                className="text-primary font-semibold text-sm uppercase tracking-wider"
              >
                Funkcje MVP
              </motion.span>
              <motion.h2
                variants={fadeUp}
                custom={1}
                className="text-3xl md:text-4xl font-bold mt-3 mb-4"
              >
                Co znajdziesz w pierwszej wersji
              </motion.h2>
              <motion.p
                variants={fadeUp}
                custom={2}
                className="text-muted-foreground text-lg max-w-2xl mx-auto"
              >
                Kluczowe narzędzia gotowe od startu — rozwijane na podstawie Twojego feedbacku
              </motion.p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-50px' }}
              variants={stagger}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {[
                {
                  icon: FileText,
                  title: 'Orzeczenia lekarskie',
                  desc: 'Generowanie orzeczeń zgodnych z rozporządzeniem Ministra Zdrowia. Numeracja i archiwum.',
                  color: 'text-primary',
                  bg: 'bg-primary/10',
                },
                {
                  icon: Calendar,
                  title: 'Kalendarz wizyt',
                  desc: 'Planowanie wizyt z widokiem timeline. Zarządzanie grafikiem lekarzy.',
                  color: 'text-accent',
                  bg: 'bg-accent/10',
                },
                {
                  icon: Users,
                  title: 'Baza pacjentów',
                  desc: 'Kartoteka pacjentów z historią badań, grupami i wyszukiwaniem.',
                  color: 'text-secondary',
                  bg: 'bg-secondary/10',
                },
                {
                  icon: Activity,
                  title: 'Formularze badań',
                  desc: 'Cyfrowe formularze badań — wzrok, słuch, badanie fizykalne, konsultacje.',
                  color: 'text-primary',
                  bg: 'bg-primary/10',
                },
                {
                  icon: ShieldCheck,
                  title: 'Ankieta pacjenta (QR)',
                  desc: 'Pacjent wypełnia ankietę przed wizytą przez telefon — dane trafiają do systemu.',
                  color: 'text-accent',
                  bg: 'bg-accent/10',
                },
                {
                  icon: Zap,
                  title: 'Fiskalizacja online',
                  desc: 'Integracja z eParagony.pl — paragony bez fizycznej drukarki fiskalnej.',
                  color: 'text-secondary',
                  bg: 'bg-secondary/10',
                },
              ].map((feature, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  custom={i}
                  className="group relative p-6 rounded-2xl bg-card border border-border/60 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300"
                >
                  <div
                    className={`w-11 h-11 ${feature.bg} rounded-xl flex items-center justify-center mb-4`}
                  >
                    <feature.icon className={`h-5 w-5 ${feature.color}`} />
                  </div>
                  <h3 className="text-lg font-semibold mb-2 text-foreground">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{feature.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* How it works */}
        <section id="how-it-works" className="py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-100px' }}
              variants={stagger}
              className="text-center mb-16"
            >
              <motion.span
                variants={fadeUp}
                className="text-primary font-semibold text-sm uppercase tracking-wider"
              >
                Proces
              </motion.span>
              <motion.h2
                variants={fadeUp}
                custom={1}
                className="text-3xl md:text-4xl font-bold mt-3"
              >
                Jak to działa?
              </motion.h2>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-50px' }}
              variants={stagger}
              className="max-w-4xl mx-auto"
            >
              {[
                {
                  step: '01',
                  title: 'Załóż konto gabinetu',
                  desc: 'Rejestracja trwa minutę. Dodaj dane gabinetu, lekarzy i personel — system jest gotowy do pracy.',
                },
                {
                  step: '02',
                  title: 'Rejestruj i badaj pacjentów',
                  desc: 'Pacjent wypełnia ankietę przez QR kod na telefonie. Ty planujesz wizytę i prowadzisz badanie w cyfrowym formularzu.',
                },
                {
                  step: '03',
                  title: 'Wydaj orzeczenie jednym kliknięciem',
                  desc: 'System generuje orzeczenie lekarskie zgodne z obowiązującymi wzorami, numeruje je i archiwizuje w PDF.',
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  custom={i}
                  className="flex gap-6 md:gap-8 mb-12 last:mb-0 group"
                >
                  <div className="flex-shrink-0 relative">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center text-primary-foreground font-bold text-lg shadow-lg shadow-primary/20">
                      {item.step}
                    </div>
                    {i < 2 && (
                      <div className="absolute top-16 left-1/2 -translate-x-1/2 w-px h-12 bg-gradient-to-b from-primary/30 to-transparent" />
                    )}
                  </div>
                  <div className="pt-2">
                    <h3 className="text-xl font-semibold mb-2 text-foreground">{item.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/50 py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <img
                  src="/lovable-uploads/39c9b322-1952-4f60-949b-38a0a810a383.png"
                  alt="MediCert Logo"
                  className="h-10 w-auto"
                />
              </div>
              <p className="text-muted-foreground text-sm max-w-sm leading-relaxed">
                Pierwszy w Polsce kompletny system dla gabinetów badań kierowców. W fazie beta —
                start Q2 2026.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sm text-foreground mb-4">Informacje</h3>
              <ul className="space-y-2.5">
                {[
                  { label: 'Funkcje', href: '#features' },
                  { label: 'Jak to działa', href: '#how-it-works' },
                ].map(item => (
                  <li key={item.label}>
                    <a
                      href={item.href}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-sm text-foreground mb-4">Kontakt</h3>
              <ul className="space-y-2.5">
                <li>
                  <a
                    href="mailto:kontakt@medicert.pl"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    kontakt@medicert.pl
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-border/50 mt-10 pt-8 text-center">
            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} MediCert. Wszelkie prawa zastrzeżone.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
